import React from 'react';
import { CheckCircle, Award, Users, Target, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BBBSeal } from '../components/BBBSeal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzfGVufDB8fHx8MTc3NDQ4Mjc2N3ww&ixlib=rb-4.1.0&q=85"
            alt="About Us Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">About Sat Drywall Ltd</h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Your trusted partner in professional drywall and insulation services
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 text-center">Our Story</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Since opening our doors in 2007, Sat Drywall Ltd has built a reputation for excellence in commercial and residential drywall and insulation services. Our journey began with a simple mission: to provide superior craftsmanship and exceptional customer service.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Today, we're proud to have completed over 500 projects, earning the trust of homeowners, contractors, and businesses throughout the region. Our commitment to quality, punctuality, and professionalism sets us apart in the construction industry.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Every project we undertake is a testament to our dedication to excellence. We don't just install drywall and insulation – we create spaces that our clients are proud to call home or workplace.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Our Mission & Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Excellence</h3>
                <p className="text-gray-600">
                  We strive for perfection in every project, no matter the size
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Integrity</h3>
                <p className="text-gray-600">
                  Honest, transparent communication and business practices
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Commitment</h3>
                <p className="text-gray-600">
                  Dedicated to delivering on time and within budget
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Quality</h3>
                <p className="text-gray-600">
                  Premium materials and superior craftsmanship guaranteed
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Why Work With Us</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Experience the Sat Drywall difference
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex gap-4 items-start p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Open Since 2007</h3>
                  <p className="text-gray-600">
                    Nearly two decades of proven expertise in drywall and insulation services across residential and commercial projects.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Licensed & Insured</h3>
                  <p className="text-gray-600">
                    Fully licensed and insured for your complete peace of mind and protection on every project.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">BBB Accredited Business</h3>
                  <p className="text-gray-600 mb-3">
                    Better Business Bureau accredited with a commitment to ethical business practices and customer satisfaction.
                  </p>
                  <BBBSeal
                    className="w-[300px] max-w-full rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300"
                    testId="bbb-seal-about"
                  />
                </div>
              </div>

              <div className="flex gap-4 items-start p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Skilled Professionals</h3>
                  <p className="text-gray-600">
                    Our team consists of highly trained and experienced professionals committed to quality workmanship.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Guarantee</h3>
                  <p className="text-gray-600">
                    We stand behind our work with a 100% satisfaction guarantee on all projects.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Competitive Pricing</h3>
                  <p className="text-gray-600">
                    Fair, transparent pricing with detailed quotes and no hidden fees or surprises.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Customer First</h3>
                  <p className="text-gray-600">
                    Your satisfaction is our priority. We listen to your needs and exceed expectations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#00AEEF] to-[#0096d1] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to Work Together?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Let's discuss your project and see how we can help bring your vision to life
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-white text-[#00AEEF] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl"
            >
              Get Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              onClick={() => navigate('/services')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#00AEEF] px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
            >
              View Our Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
